
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";

// The correct password - in a real app, this would be handled securely on the backend
const CORRECT_PASSWORD = "bubblecare2025";

// Schema for the password form
const passwordSchema = z.object({
  password: z.string().min(1, "Password is required"),
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

interface PasswordProtectionProps {
  children: React.ReactNode;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
    },
  });

  useEffect(() => {
    // Check if already authenticated via localStorage
    const isAuth = localStorage.getItem("bubblecare-authenticated") === "true";
    setIsAuthenticated(isAuth);
    setIsDialogOpen(!isAuth);
  }, []);

  const onSubmit = (data: PasswordFormValues) => {
    if (data.password === CORRECT_PASSWORD) {
      localStorage.setItem("bubblecare-authenticated", "true");
      setIsAuthenticated(true);
      setIsDialogOpen(false);
      toast.success("Access granted", {
        description: "Welcome to BubbleCare!",
      });
    } else {
      toast.error("Access denied", {
        description: "Incorrect password, please try again.",
      });
      form.reset();
    }
  };

  // If user is authenticated, show the children components
  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">Password Protected</DialogTitle>
          <DialogDescription className="text-center">
            Please enter the password to access BubbleCare.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input 
                        placeholder="Enter password" 
                        type={showPassword ? "text" : "password"} 
                        autoComplete="current-password"
                        {...field} 
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-10 w-10"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showPassword ? "Hide password" : "Show password"}
                      </span>
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full">
              Access App
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordProtection;
